import { cn } from "@nt/ui/cn";
import {
  deltaTone,
  formatDelta,
  type DeltaGoodWhen,
} from "@nt/ui/metric-block";
import type { ComponentType, ReactNode } from "react";

/**
 * Percent change from `previous` to `current` (12.5 = up 12.5%), or null when
 * there is no previous figure to compare with.
 */
export function pctChange(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

const TONE = {
  good: "text-success",
  bad: "text-danger",
  flat: "text-muted-foreground",
} as const;

/**
 * One KPI: label, figure, and the change against the previous period. A grid
 * of tiles stands on the page by itself; never put tiles inside a card.
 */
export function StatTile({
  label,
  value,
  delta,
  days = 30,
  goodWhen = "up",
  hint,
  icon: Icon,
  className,
}: {
  label: string;
  /** Already formatted, e.g. "$120.00" or "4.2%". */
  value: string;
  /** Percent change (see `pctChange`); null = no previous period; omit for no delta line. */
  delta?: number | null;
  /** The range the delta compares, for "vs previous N days". */
  days?: number;
  goodWhen?: DeltaGoodWhen;
  hint?: ReactNode;
  icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  className?: string;
}) {
  // A figure that did not move says so; "0.0%" reads like a measurement.
  const text =
    delta === undefined ? null : delta === 0 ? "No change" : formatDelta(delta);
  const tone = delta === undefined ? "flat" : deltaTone(delta, goodWhen);
  const caption = `vs previous ${days === 365 ? "12 months" : `${days} days`}`;
  return (
    <div
      data-slot="stat-tile"
      className={cn(
        "flex min-w-0 flex-col gap-1 rounded-card border border-border bg-card p-4",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 text-sm text-muted-foreground">{label}</p>
        {Icon ? (
          <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        ) : null}
      </div>
      <p className="type-title text-foreground tabular-nums">{value}</p>
      {delta === undefined ? null : text === null ? (
        <p className="text-xs text-muted-foreground">No previous period</p>
      ) : (
        <p
          className={cn(
            "text-xs font-medium tabular-nums",
            TONE[delta === 0 ? "flat" : tone],
          )}
        >
          {text}{" "}
          <span className="font-normal text-muted-foreground">{caption}</span>
        </p>
      )}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
